import { Link } from "react-router-dom";
import { ArrowRight, Check, ClipboardList, FileDown, Link2, MousePointerClick, Target, Users } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useAuth } from "@/context/AuthContext";
import { AppHeader, PageShell } from "@/components/AppHeader";
import { LandingDemo } from "@/components/landing/LandingDemo";
import { SiteFooter } from "@/components/SiteFooter";
import { FREE_FEATURES, TEAM_FEATURES } from "@/lib/plans";

const FEATURES = [
  { icon: MousePointerClick, color: "#dc2626", title: "Tap to hit", text: "Tap anywhere. The ball flies and all nine defenders run to their spots, with throws drawn in." },
  { icon: ClipboardList, color: "#2563eb", title: "Your playbook", text: "Drag any player, pick their job, add a note. Your version replaces the default for your team." },
  { icon: Link2, color: "#7c3aed", title: "One link for the team", text: "Players and parents open a link or a 6-letter code. No accounts, works on any phone." },
  { icon: Target, color: "#0d9488", title: "Practice + leaderboard", text: "Kids get a situation and answer 'Where do I go?'. You see what they miss and assign drills." },
  { icon: FileDown, color: "#4b5563", title: "Print it", text: "Export plays as PDFs or wristband cards for the dugout." },
  { icon: Users, color: "#ea580c", title: "Baseball + softball", text: "Little League 46/60, standard 60/90, and 60 ft softball fields." },
];

const FAQ = [
  ["Is it really free to try?", "Yes. The baseball field with default plays is free forever. Coaches get every Team feature free for 14 days, no card needed."],
  ["Where do the plays come from?", "A rules-based playbook built from standard youth defense: force plays, base covers, cutoffs, relays and backups. Every default is marked for coach review so you can make it yours."],
  ["Do players need an account?", "No. Players and parents open your team link or type the code. For practice they just enter a first name."],
  ["Does it work in the sun?", "It's built for the dugout: big tap targets, high-contrast colors, and a light mode that stays readable outdoors."],
  ["Can I use it for more than one team?", "Yes. Make as many teams as you like and duplicate a playbook for a new season or age group."],
  ["How do I cancel?", "From the Pricing page. Your plan stays active until the end of the billing period."],
];

export default function LandingPage() {
  const { user } = useAuth();
  return (
    <PageShell>
      <AppHeader>
        <div className="hidden sm:flex gap-2">
          <Link to="/pricing" className="nav-btn" data-testid="landing-nav-pricing">Pricing</Link>
          <Link to="/join" className="nav-btn" data-testid="landing-nav-join">Join team</Link>
        </div>
        <Link to={user ? "/coach" : "/login"} className="nav-btn" data-testid="landing-nav-coach">{user ? "My teams" : "Coach login"}</Link>
      </AppHeader>
      <section className="land-hero pt-4 sm:pt-8">
        <div className="space-y-5 fade-up">
          <p className="ctl-label">Youth baseball + softball defense</p>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold uppercase tracking-tight leading-[0.92]" data-testid="landing-headline">
            Every kid knows<br />where to go.
          </h1>
          <p className="text-base sm:text-lg font-medium text-[var(--muted)] max-w-lg">
            Tap where the ball goes. DiamondPlay shows all nine defenders running to their spot, who covers, who backs up, and where the throw goes.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/field" className="btn-primary !text-lg !px-6" data-testid="landing-try-field">Try the field <ArrowRight size={20} /></Link>
            <Link to={user ? "/coach" : "/login"} className="btn-ghost !text-lg !px-6" data-testid="landing-start-trial">Start 14-day free trial</Link>
            <Link to="/join" className="btn-ghost !text-lg !px-6 sm:hidden" data-testid="landing-join-mobile">Join team</Link>
          </div>
          <p className="text-sm font-semibold text-[var(--muted)]">No card required.</p>
        </div>
        <div className="fade-up" style={{ animationDelay: "150ms" }}><LandingDemo /></div>
      </section>

      <section className="land-section" data-testid="landing-features">
        <h2 className="font-heading text-4xl font-extrabold uppercase tracking-tight mb-6">Built for the dugout</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => (
            <div key={f.title} className="card feature-tile space-y-2 fade-up" style={{ "--tile": f.color, animationDelay: `${i * 70}ms` }}>
              <f.icon size={28} style={{ color: f.color }} />
              <h3 className="font-heading text-2xl font-bold uppercase">{f.title}</h3>
              <p className="font-medium text-[var(--muted)]">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="pricing" className="land-section" data-testid="landing-pricing">
        <h2 className="font-heading text-4xl font-extrabold uppercase tracking-tight mb-6">Simple pricing</h2>
        <div className="grid gap-4 md:grid-cols-2 max-w-4xl">
          <div className="card space-y-3 !p-6">
            <h3 className="font-heading text-3xl font-extrabold uppercase">Free</h3>
            <p className="font-heading text-5xl font-extrabold">$0</p>
            <ul className="space-y-2">{FREE_FEATURES.map((t) => <li key={t} className="flex gap-2 font-semibold"><Check size={20} className="text-[var(--accent)] shrink-0" />{t}</li>)}</ul>
          </div>
          <div className="card space-y-3 !p-6 plan-team">
            <h3 className="font-heading text-3xl font-extrabold uppercase">Team</h3>
            <p className="font-heading text-5xl font-extrabold">$9.99<span className="text-xl text-[var(--muted)]">/mo</span> <span className="text-2xl">or $79/yr</span></p>
            <ul className="space-y-2">{TEAM_FEATURES.map((t) => <li key={t} className="flex gap-2 font-semibold"><Check size={20} className="text-[var(--accent)] shrink-0" />{t}</li>)}</ul>
            <Link to="/pricing" className="btn-primary w-full" data-testid="landing-see-plans">See plans</Link>
          </div>
        </div>
      </section>

      <section className="land-section max-w-3xl" data-testid="landing-faq">
        <h2 className="font-heading text-4xl font-extrabold uppercase tracking-tight mb-4">FAQ</h2>
        <Accordion type="single" collapsible className="card !py-1">
          {FAQ.map(([q, a], i) => (
            <AccordionItem key={q} value={`q${i}`} className={i === FAQ.length - 1 ? "border-b-0" : ""}>
              <AccordionTrigger className="text-left text-base font-bold" data-testid={`faq-trigger-${i}`}>{q}</AccordionTrigger>
              <AccordionContent className="text-base font-medium text-[var(--muted)]">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
      <SiteFooter />
    </PageShell>
  );
}
